import { randomUUID } from '../uuid.js';
import { cloneBoard, PROPERTY_TYPES } from '../data/board.js';
import { Property } from './Property.js';
import { Player } from './Player.js';

const DEFAULT_SETTINGS = Object.freeze({
  startingMoney: 1500,
  startSalary: 200,
  jailFine: 50,
  maxPlayers: 4,
});

export class Game {
  constructor({ id = randomUUID(), players = [], settings = {} } = {}) {
    this.id = id;
    this.status = 'waiting';
    this.players = players;
    this.board = cloneBoard();
    this.properties = this.board
      .filter((cell) => PROPERTY_TYPES.has(cell.type))
      .map((cell) => new Property(cell));
    this.currentPlayerIndex = 0;
    this.dice = { values: [1, 1], total: 2, isDouble: true, sequence: 0 };
    this.doublesCount = 0;
    this.extraTurn = false;
    this.phase = 'waiting';
    this.pendingPurchase = null;
    this.pendingDebt = null;
    this.trades = [];
    this.log = [];
    this.winner = null;
    this.lastCard = null;
    this.lastMove = null;
    this.processedActions = [];
    this.revision = 0;
    this.settings = { ...DEFAULT_SETTINGS, ...settings };
  }

  get currentPlayer() {
    return this.players[this.currentPlayerIndex] ?? null;
  }

  getPlayer(playerId) {
    return this.players.find((player) => player.id === playerId) ?? null;
  }

  getProperty(propertyId) {
    return this.properties.find((property) => property.id === Number(propertyId)) ?? null;
  }

  addLog(message, type = 'info') {
    this.log.push({
      id: randomUUID(),
      message,
      type,
      createdAt: Date.now(),
    });
    if (this.log.length > 120) {
      this.log.splice(0, this.log.length - 120);
    }
  }

  findProcessedAction(playerId, actionId) {
    return this.processedActions.find((action) => action.playerId === playerId && action.actionId === actionId) ?? null;
  }

  recordProcessedAction(playerId, actionId, eventName) {
    this.processedActions.push({ playerId, actionId, eventName, processedAt: Date.now() });
    if (this.processedActions.length > 240) this.processedActions.splice(0, this.processedActions.length - 240);
  }

  toSnapshot() {
    return {
      id: this.id,
      status: this.status,
      players: this.players.map((player) => player.toSnapshot()),
      board: this.board.map((cell) => ({ ...cell, rents: cell.rents ? [...cell.rents] : undefined })),
      properties: this.properties.map((property) => property.toSnapshot()),
      currentPlayerIndex: this.currentPlayerIndex,
      dice: { ...this.dice, values: [...this.dice.values] },
      doublesCount: this.doublesCount,
      extraTurn: this.extraTurn,
      phase: this.phase,
      pendingPurchase: this.pendingPurchase ? { ...this.pendingPurchase } : null,
      pendingDebt: this.pendingDebt ? structuredClone(this.pendingDebt) : null,
      trades: this.trades.map((trade) => structuredClone(trade)),
      log: this.log.map((entry) => ({ ...entry })),
      winner: this.winner ? { ...this.winner } : null,
      lastCard: this.lastCard ? structuredClone(this.lastCard) : null,
      lastMove: this.lastMove ? structuredClone(this.lastMove) : null,
      processedActions: this.processedActions.map((action) => ({ ...action })),
      revision: this.revision,
      settings: { ...this.settings },
    };
  }

  static fromSnapshot(snapshot, PlayerClass = Player) {
    const game = new this({
      id: snapshot.id,
      players: (snapshot.players ?? []).map((player) => PlayerClass.fromSnapshot(player)),
      settings: snapshot.settings,
    });
    game.status = snapshot.status ?? 'waiting';
    if (Array.isArray(snapshot.board) && snapshot.board.length === game.board.length) {
      game.board = snapshot.board.map((cell) => ({ ...cell, rents: cell.rents ? [...cell.rents] : undefined }));
    }
    const propertySnapshots = new Map((snapshot.properties ?? []).map((property) => [Number(property.id), property]));
    game.properties = game.board
      .filter((cell) => PROPERTY_TYPES.has(cell.type))
      .map((cell) => new Property(cell, propertySnapshots.get(cell.index)));
    game.currentPlayerIndex = Number(snapshot.currentPlayerIndex ?? 0);
    game.dice = {
      values: [...(snapshot.dice?.values ?? [1, 1])],
      total: Number(snapshot.dice?.total ?? 2),
      isDouble: Boolean(snapshot.dice?.isDouble),
      sequence: Number(snapshot.dice?.sequence ?? 0),
    };
    game.doublesCount = Number(snapshot.doublesCount ?? 0);
    game.extraTurn = Boolean(snapshot.extraTurn);
    game.phase = snapshot.phase ?? 'waiting';
    game.pendingPurchase = snapshot.pendingPurchase ? { ...snapshot.pendingPurchase } : null;
    game.pendingDebt = snapshot.pendingDebt ? structuredClone(snapshot.pendingDebt) : null;
    game.trades = (snapshot.trades ?? []).map((trade) => structuredClone(trade));
    game.log = (snapshot.log ?? []).map((entry) => ({ ...entry }));
    game.winner = snapshot.winner ? { ...snapshot.winner } : null;
    game.lastCard = snapshot.lastCard ? structuredClone(snapshot.lastCard) : null;
    game.lastMove = snapshot.lastMove ? structuredClone(snapshot.lastMove) : null;
    game.processedActions = (snapshot.processedActions ?? []).map((action) => ({ ...action }));
    game.revision = Number(snapshot.revision ?? 0);
    return game;
  }

  getPublicState(viewerId) {
    return {
      id: this.id,
      status: this.status,
      players: this.players.map((player) => player.toPublic()),
      board: this.board,
      properties: this.properties.map((property) => property.toPublic()),
      currentPlayerIndex: this.currentPlayerIndex,
      currentPlayerId: this.currentPlayer?.id ?? null,
      currentPlayer: this.currentPlayer?.toPublic() ?? null,
      dice: { ...this.dice, values: [...this.dice.values] },
      doublesCount: this.doublesCount,
      extraTurn: this.extraTurn,
      phase: this.phase,
      pendingPurchase: this.pendingPurchase ? { ...this.pendingPurchase } : null,
      pendingDebt: this.pendingDebt ? { ...this.pendingDebt } : null,
      trades: this.trades
        .filter((trade) => trade.status === 'pending' && [trade.fromPlayerId, trade.toPlayerId].includes(viewerId))
        .map((trade) => ({ ...trade, offeredPropertyIds: [...trade.offeredPropertyIds], requestedPropertyIds: [...trade.requestedPropertyIds] })),
      log: this.log.slice(-60),
      winner: this.winner ? { ...this.winner } : null,
      lastCard: this.lastCard ? { ...this.lastCard } : null,
      lastMove: this.lastMove ? structuredClone(this.lastMove) : null,
      settings: { ...this.settings },
      revision: this.revision,
    };
  }
}
